import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dia, EquipaDia, SemanaDia } from '@/types';
import { addDays, startOfWeek } from '@/lib/format';
import * as api from './api';

const keys = {
  dia: (data: string, userId?: string) => ['partes', 'dia', data, userId ?? 'me'] as const,
  equipaDia: (data: string) => ['partes', 'equipaDia', data] as const,
  semana: (data: string, userId?: string) => ['partes', 'semana', data, userId ?? 'me'] as const,
};

export function useDia(data: string, userId?: string, enabled = true) {
  return useQuery<Dia>({
    queryKey: keys.dia(data, userId),
    queryFn: () => api.fetchDia(data, userId),
    enabled,
  });
}

export function useEquipaDia(data: string, enabled = true) {
  return useQuery<EquipaDia>({
    queryKey: keys.equipaDia(data),
    queryFn: () => api.fetchEquipaDia(data),
    enabled,
  });
}

/**
 * The day strip, as one flat run of days ending on the week of `ancora` and
 * reaching `semanas` weeks back — the strip scrolls into the past, so it needs
 * more than the current Monday–Sunday.
 *
 * Each week is its own query, keyed by its Monday: walking further back only
 * fetches what is new and leaves the weeks already on screen alone.
 */
export function useSemanas(ancora: string, semanas: number, userId?: string) {
  const inicios = useMemo(() => {
    const base = startOfWeek(ancora);
    return Array.from({ length: Math.max(1, semanas) }, (_, i) =>
      addDays(base, -7 * (Math.max(1, semanas) - 1 - i)),
    );
  }, [ancora, semanas]);

  return useQueries({
    queries: inicios.map((inicio) => ({
      queryKey: keys.semana(inicio, userId),
      queryFn: () => api.fetchSemana(inicio, userId),
    })),
    combine: (results) => ({
      dias: results.flatMap((r) => r.data ?? ([] as SemanaDia[])),
      isLoading: results.some((r) => r.isLoading),
      isFetching: results.some((r) => r.isFetching),
      error: results.find((r) => r.error)?.error ?? null,
    }),
  });
}

/**
 * Every write invalidates the whole parte/mapa/obra surface: a single entry moves
 * the day total, the week strip, the monthly map and the obra's month-to-date.
 */
function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['partes'] });
    void queryClient.invalidateQueries({ queryKey: ['mapas'] });
    void queryClient.invalidateQueries({ queryKey: ['obras'] });
  };
}

export function useCreateRegisto() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: api.createRegisto, onSuccess: invalidate });
}

export function useUpdateRegisto() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof api.updateRegisto>[1]) =>
      api.updateRegisto(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteRegisto() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: api.deleteRegisto, onSuccess: invalidate });
}

export function useSetObservacaoDia() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({
      data,
      observacao,
      userId,
    }: {
      data: string;
      observacao: string | null;
      userId?: string;
    }) => api.setObservacaoDia(data, observacao, userId),
    onSuccess: invalidate,
  });
}

export function useToggleFecho() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ data, userId }: { data: string; userId?: string }) =>
      api.toggleFecho(data, userId),
    onSuccess: invalidate,
  });
}
